import { useEffect, useMemo, useState } from 'react';
import { MapLibreMap } from './MapLibreMap';
import { MapMarker } from './MapMarker';
import { MapPolygon } from './MapPolygon';
import { latLngPointsToGeoJsonPolygon } from '@/utils/geojson';
import { toLngLat } from '@/utils/mapConfig';

interface ServiceAreaMapProps {
  /** [lat, lng] */
  center?: [number, number];
  /** Points as [lat, lng] */
  polygon?: number[][];
  onPolygonChange?: (points: number[][]) => void;
  height?: string;
}

const DEFAULT_CENTER: [number, number] = [37.0, 35.3];

export function ServiceAreaMap({
  center = DEFAULT_CENTER,
  polygon = [],
  onPolygonChange,
  height = '320px',
}: ServiceAreaMapProps) {
  const [points, setPoints] = useState<number[][]>(polygon);

  useEffect(() => {
    setPoints(polygon);
  }, [polygon]);

  const geoJsonPolygon = useMemo(() => latLngPointsToGeoJsonPolygon(points), [points]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (points.length > 0) {
      return toLngLat([points[0][0], points[0][1]]);
    }
    return toLngLat(center);
  }, [center, points]);

  const addPoint = (lng: number, lat: number) => {
    const next = [...points, [lat, lng]];
    setPoints(next);
    onPolygonChange?.(next);
  };

  const clear = () => {
    setPoints([]);
    onPolygonChange?.([]);
  };

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-xl border" style={{ height }}>
        <MapLibreMap
          center={mapCenter}
          zoom={11}
          onMapClick={onPolygonChange ? addPoint : undefined}
        >
          {geoJsonPolygon && <MapPolygon polygon={geoJsonPolygon} />}
          {points.map(([lat, lng], i) => (
            <MapMarker key={`${lat}-${lng}-${i}`} position={toLngLat([lat, lng])} />
          ))}
        </MapLibreMap>
      </div>
      {onPolygonChange && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Haritaya tıklayarak polygon noktası ekleyin ({points.length} nokta)</span>
          <button type="button" className="text-red-600 hover:underline" onClick={clear}>
            Temizle
          </button>
        </div>
      )}
    </div>
  );
}

interface LocationMapModalProps {
  open: boolean;
  onClose: () => void;
  latitude?: number;
  longitude?: number;
  onSelect?: (lat: number, lng: number) => void;
}

export function LocationMapModal({
  open,
  onClose,
  latitude,
  longitude,
  onSelect,
}: LocationMapModalProps) {
  const [pos, setPos] = useState<[number, number] | null>(
    latitude != null && longitude != null ? [latitude, longitude] : null,
  );

  useEffect(() => {
    if (latitude != null && longitude != null) {
      setPos([latitude, longitude]);
    }
  }, [latitude, longitude, open]);

  if (!open) return null;

  const mapCenter = pos ? toLngLat(pos) : toLngLat(DEFAULT_CENTER);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Kapat" />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-4 shadow-xl">
        <h2 className="mb-3 text-lg font-semibold">Konum Seç</h2>
        <div className="h-72 overflow-hidden rounded-xl border">
          <MapLibreMap
            center={mapCenter}
            zoom={13}
            showGeolocate
            onMapClick={(lng, lat) => {
              setPos([lat, lng]);
              onSelect?.(lat, lng);
            }}
          >
            {pos && <MapMarker position={toLngLat(pos)} iconType="delivery" />}
          </MapLibreMap>
        </div>
        {pos && (
          <p className="mt-2 text-xs text-gray-500">
            {pos[0].toFixed(5)}, {pos[1].toFixed(5)}
          </p>
        )}
        <button type="button" className="mt-3 text-sm text-brand-600" onClick={onClose}>
          Kapat
        </button>
      </div>
    </div>
  );
}
